import React, { useEffect, useState } from "react";
import { LayoutGrid, ListChecks, Plus, Users, Award, FileText, LogOut, ExternalLink, MessageSquare, Clock, Users2, ShieldUser, Tags, BookOpen, Activity, Trophy, Ban, Megaphone, Siren, CalendarClock, Forward, GraduationCap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/common/Brand";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export function ModSidebar({ view, setView }) {
  const { user, logout, has } = useAuth();
  const { tr } = useLang();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const canMod = has("MODERATE");

  useEffect(() => {
    if (!canMod) return undefined;
    const load = () => api.get("/mod/disputes/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    window.addEventListener("disputes-seen", load);
    return () => { clearInterval(id); window.removeEventListener("disputes-seen", load); };
  }, [canMod]);

  const sections = [
    {
      label: "Workspace",
      items: [
        { key: "overview", label: tr.mTabReports === "Reports" ? "Overview" : "Overview", icon: LayoutGrid, show: true },
        { key: "reports", label: tr.mTabReports, icon: ListChecks, show: has("MODERATE") },
        { key: "disputes", label: "Disputes", icon: MessageSquare, show: has("MODERATE"), badge: unread },
        { key: "emergency", label: "Emergency line", icon: Siren, show: has("MODERATE") },
        { key: "data", label: "Adatigénylés", icon: ShieldUser, show: has("MODERATE") },
        { key: "new", label: tr.mTabNew, icon: Plus, show: has("CREATE_REPORTS") },
      ],
    },
    {
      label: "Administration",
      items: [
        { key: "agents", label: tr.mTabAgents, icon: Users, show: user.role === "admin" },
        { key: "ranks", label: tr.mTabRanks, icon: Award, show: user.role === "admin" },
        { key: "sla", label: "SLA", icon: Clock, show: user.role === "admin" },
        { key: "statuses", label: "Státuszok", icon: Tags, show: user.role === "admin" },
        { key: "guidelines", label: "Irányelvek", icon: BookOpen, show: user.role === "admin" },
        { key: "achievements", label: "Eredmények", icon: Trophy, show: user.role === "admin" },
        { key: "bans", label: "IP tiltások", icon: Ban, show: user.role === "admin" },
        { key: "notices", label: "Közlemények", icon: Megaphone, show: user.role === "admin" },
        { key: "schedule", label: "Beosztás", icon: CalendarClock, show: user.role === "admin" },
        { key: "forwarding", label: "Report továbbító", icon: Forward, show: user.role === "admin" },
        { key: "academy", label: "Akadémia", icon: GraduationCap, show: user.role === "admin" },
        { key: "logs", label: "Napló", icon: Activity, show: user.role === "admin" },
        { key: "team", label: "Csapat", icon: Users2, show: user.role === "admin" },
        { key: "pages", label: "Legal Pages", icon: FileText, show: user.role === "admin" },
      ],
    },
  ];

  return (
    <aside className="hidden w-[240px] shrink-0 flex-col border-r border-zinc-800/70 bg-zinc-950 md:flex">
      <div className="flex h-16 items-center border-b border-zinc-800/70 px-4">
        <Logo className="h-9" />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((s) => {
          const items = s.items.filter((i) => i.show);
          if (!items.length) return null;
          return (
            <div key={s.label} className="mb-5">
              <div className="px-2 pb-2 label-caps">{s.label}</div>
              <div className="space-y-0.5">
                {items.map((i) => {
                  const active = view === i.key;
                  return (
                    <button
                      key={i.key}
                      data-testid={`mnav-${i.key}`}
                      onClick={() => setView(i.key)}
                      className={`group relative flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] transition-colors ${
                        active ? "bg-zinc-800/60 text-zinc-50" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                      }`}
                    >
                      {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-blue-500" />}
                      <i.icon size={16} className="shrink-0" />
                      <span className="font-medium">{i.label}</span>
                      {i.badge > 0 && <span data-testid={`mnav-badge-${i.key}`} className="ml-auto rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">{i.badge}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-zinc-800/70 p-3">
        <button
          onClick={() => navigate("/")}
          className="mb-2 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[12px] text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
        >
          <ExternalLink size={15} /> {tr.mBackToSite}
        </button>
        <div className="flex items-center gap-2.5 rounded-md px-1 py-1">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-zinc-800 text-[12px] font-semibold text-zinc-200">
            {(user.name || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px] font-medium text-zinc-100">{user.name}</div>
            <div className="truncate text-[11px] text-zinc-500">{user.role === "admin" ? tr.mAdmin : (user.rank || tr.mAgentRole)}</div>
          </div>
          <button onClick={logout} data-testid="mod-logout" title={tr.mLogout} className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-red-400">
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
}
